import { createSelector } from '@reduxjs/toolkit';
import { cloneDeep, get, isEmpty, isNil, merge } from 'lodash';
import { useSelector } from 'react-redux';

import {
  clearAllOverrides,
  resetClientFeatureFlags,
  setOverride,
  surveyDebuggerSelector,
  updateClientFeatureFlag
} from '@redux/reducers/debugger';
import { RootState, useAppDispatch, useAppSelector } from '@redux/store';

import DebuggerService from '@services/debugger';
import {
  ClientSideRenderingFeatureFlag,
  normalizeSurveyEngineFeatureFlagsList
} from '@services/featureFlags';

import { isElement } from '@utils/domNodeHelpers';

//----------------------------------------------------------------------
// TYPES
//----------------------------------------------------------------------

export interface SpecStats {
  componentsHidden: number;
  componentsVisible: number;
  componentsTotal: number;
  conditionsHidden: number;
  conditionsLogic: number;
  conditionsTotal: number;
}

interface ValidationScriptBlock {
  blockContent: string;
  blockType: string;
  field: string | null;
}

//----------------------------------------------------------------------
// CONFIG
//----------------------------------------------------------------------

export const SURVEY_DEBUGGER_STORAGE_KEY = 'survey_debugger';

const ASK_NOW_NODE_NAME = 'innovation-testing-page';

export const defaultStats: SpecStats = {
  componentsHidden: 0,
  componentsVisible: 0,
  componentsTotal: 0,
  conditionsHidden: 0,
  conditionsLogic: 0,
  conditionsTotal: 0
};

export const defaultAskNowInfo = {
  askNow: {
    altSetsCount: 0,
    version: '',
    stats: cloneDeep(defaultStats),
    condition: '',
    isAskNowValueNodeAvailable: false,
    unitName: '',
    language: '',
    availableLanguages: '',
    id: '',
    askNowPageIndex: 0,
    conditionLogicWithScript: []
  }
};

//----------------------------------------------------------------------
// BOOTSTRAPPING
//----------------------------------------------------------------------

export function isSurveysRuntimeDebuggerEnabled() {
  return get(
    window,
    ['surveyData', 'featureFlags', '24cr4_MPC8530_surveys_runtime_debugger'],
    false
  );
}

export function bootstrapDebuggerOverrides() {
  const debuggerOverrides = localStorage.getItem(SURVEY_DEBUGGER_STORAGE_KEY);

  if (!isNil(debuggerOverrides) && isSurveysRuntimeDebuggerEnabled()) {
    const id = window.surveyData.specId || window.surveyData.surveyid;
    const newSurveyData = merge(window.surveyData, JSON.parse(debuggerOverrides)[id]);
    window.surveyData = newSurveyData;
  }
}

//----------------------------------------------------------------------
// PARSERS
//----------------------------------------------------------------------

/**
 * Extracts the alphabetic word from a string pattern like "100100-merlin-692".
 * Removes numbers and symbols, returning only the part likely to be  tenant nickname.
 */

export function extractCompanyNameFromSpecId(input: string | null) {
  if (isNil(input) || isEmpty(input)) {
    return null;
  }

  const parts = input.split('-');

  for (const part of parts) {
    if (/^[a-zA-Z]+$/.test(part)) {
      return part;
    }
  }

  return null;
}

export function parseCompanyName(specNode: Element) {
  const companyName = specNode.getAttribute('company-name');
  if (!isNil(companyName)) {
    return companyName;
  }

  // Some specs might not have a `company-name` attribute.
  const cookieID = specNode.getAttribute('cookie');
  if (!isNil(cookieID)) {
    return cookieID;
  }

  // Some specs might not have `cookie` attribute.
  const specID = specNode.getAttribute('id');
  const tenantNicknameFromId = extractCompanyNameFromSpecId(specID);
  if (!isNil(tenantNicknameFromId)) {
    return tenantNicknameFromId;
  }

  // client-cluster-name is tempting but not an option.
  // Sometimes, it'll be a match with a tenant nickname.
  // Sometimes, it'll be something like this:
  // client-cluster-name="cr-den-qa2-smiladminsuite23cr2-1"
  // client-cluster-name="cr-denqa2-max-release-branch-2"
  return null;
}

export function parseClusterURL(document: Document, specNode: Element) {
  const clusterUrl = specNode.getAttribute('cluster-url');

  if (!isNil(clusterUrl)) {
    return clusterUrl;
  }

  // Some instances might not have `cluster-url`.
  // We should fallback to `return-feed` and process this string.
  const returnFeed = document.getElementsByTagName('return-feed')[0];

  if (!isNil(returnFeed)) {
    const baseUrl = returnFeed.getAttribute('url')!;
    const url = new URL(baseUrl);
    const hostname = url.hostname.replace(/-be(?=\.[^/]+$)/, '');
    return `https://${hostname}`;
  }

  return null;
}

/**
 * Recursive Utility To Fetch Nodes With Conditions For Entire Component.
 */

export function getConditionCount(node: Element | ChildNode): {
  withConditionHidden: number;
  withConditionLogic: number;
} {
  let withConditionHidden = 0;
  let withConditionLogic = 0;
  if (isElement(node) && node.hasAttribute('condition')) {
    if (node.getAttribute('condition') === 'false') {
      withConditionHidden++;
    } else {
      withConditionLogic++;
    }
  }

  const children = Array.from(node.childNodes ?? []);

  for (const child of children) {
    const conditionCount = getConditionCount(child);
    withConditionHidden += conditionCount.withConditionHidden;
    withConditionLogic += conditionCount.withConditionLogic;
  }

  return { withConditionHidden, withConditionLogic };
}

/**
 * Gets Total Component Nodes Split By Hidden and Non-hidden Per Survey Page And Overall Survey.
 * Gets Total Nodes with Conditions Split By Hidden and Logic Per Survey Page And Overall Survey.
 */

export function parseStats(pageNodes: Element[]) {
  let allStats = cloneDeep(defaultStats);
  const paginatedStats: Record<number, SpecStats> = {};
  let isAskNowPagePresent = false;
  let askNowPageIndex = 0;

  pageNodes.forEach((pageNode, index) => {
    let componentsHidden = 0;
    let componentsVisible = 0;
    let conditionsHidden = 0;
    let conditionsLogic = 0;

    const immediateChildElements = Array.from(pageNode.children).filter(
      (node) => node.nodeType === 1
    );

    const hasAskNowNode = immediateChildElements.find((node) => {
      return node.nodeName === ASK_NOW_NODE_NAME;
    });

    if (hasAskNowNode) {
      isAskNowPagePresent = true;
      askNowPageIndex = index + 1;
    }

    immediateChildElements.forEach((child) => {
      if (child.nodeName === ASK_NOW_NODE_NAME) {
        return;
      }
      if (child.hasAttribute('condition')) {
        componentsHidden++;
      } else {
        componentsVisible++;
      }
      const conditionCount = getConditionCount(child);
      conditionsHidden += conditionCount.withConditionHidden;
      conditionsLogic += conditionCount.withConditionLogic;
    });

    const componentsTotal = componentsHidden + componentsVisible;
    const conditionsTotal = conditionsHidden + conditionsLogic;

    if (!hasAskNowNode) {
      paginatedStats[index + 1] = {
        componentsHidden,
        componentsVisible,
        componentsTotal,
        conditionsHidden,
        conditionsLogic,
        conditionsTotal
      };
    }

    allStats = {
      componentsHidden: allStats.componentsHidden + componentsHidden,
      componentsVisible: allStats.componentsVisible + componentsVisible,
      componentsTotal: allStats.componentsTotal + componentsTotal,
      conditionsHidden: allStats.conditionsHidden + conditionsHidden,
      conditionsLogic: allStats.conditionsLogic + conditionsLogic,
      conditionsTotal: allStats.conditionsTotal + conditionsTotal
    };
  });

  return { paginatedStats, allStats, isAskNowPagePresent, askNowPageIndex };
}

/**
 *  Recursive Utility To Fetch All The Validation Blocks Added With Javascript For Entire Survey
 */

export function parseJSValidationConditionLogic(firstMatchingChildNode: Element | ChildNode) {
  const scriptRegex = /SCRIPT\s*\((['"])([\s\S]*?)\1\)/gi;
  const results: ValidationScriptBlock[] = [];

  const attributes = ['validation', 'condition'];

  for (const attr of attributes) {
    if (isElement(firstMatchingChildNode) && firstMatchingChildNode.hasAttribute(attr)) {
      const attrValue = firstMatchingChildNode.getAttribute(attr);

      if (isNil(attrValue)) {
        continue;
      }

      const fieldValue = firstMatchingChildNode.getAttribute('field');

      for (const scriptMatch of Array.from(attrValue.matchAll(scriptRegex))) {
        results.push({
          blockType: firstMatchingChildNode.nodeName,
          blockContent: scriptMatch[2],
          field: fieldValue
        });
      }
    }
  }

  const children = Array.from(firstMatchingChildNode.childNodes ?? []);

  for (const child of children) {
    results.push(...parseJSValidationConditionLogic(child));
  }

  return results;
}

export function exportToJSON<T>({ data, label, id }: { data: T; label: string; id?: string }) {
  try {
    const jsonString = JSON.stringify(data, null, 4).replace(/<\/script>/gi, '<\\/script>'); // Pretty print JSON
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `[${label}]${id || 'unknown'}.json`;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export JSON:', error);
  }
}

export function exportToXML({ data, label, id }: { data: string; label: string; id?: string }) {
  try {
    const blob = new Blob([data], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `[${label}]${id || 'unknown'}.xml`;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export XML:', error);
  }
}

export function parseAskNow(
  surveyNodeChildren: Element[],
  fields: Element[],
  isAskNowPagePresent: boolean,
  document: Document,
  askNowPageIndex: number
) {
  let askNowRawXml: string | null = null;

  if (!isAskNowPagePresent) {
    return defaultAskNowInfo;
  }

  const askNowNode = surveyNodeChildren.find(
    (node) => node.nodeName === 'innovation-testing-spec'
  ) as Element;

  const latestAskNowField = Array.from(fields).find((node) => {
    return node.getAttribute('field') === 'm__innovation_test_hint';
  }) as Element;

  const legacyAskNowField = Array.from(fields).find((node) => {
    return node.getAttribute('field') === 'm__innovation_test';
  }) as Element;

  const askNowValueField = latestAskNowField || legacyAskNowField;
  if (askNowNode) {
    askNowRawXml = askNowNode.outerHTML;
  } else if (askNowValueField) {
    askNowRawXml = askNowValueField.getAttribute('value') as string;
  }

  if (askNowRawXml) {
    const askNowContent = new window.DOMParser().parseFromString(askNowRawXml, 'text/xml');
    const conditionLogicWithScript = Array.from(askNowContent.childNodes).flatMap(
      (firstMatchingChildNode: any) => parseJSValidationConditionLogic(firstMatchingChildNode) || []
    );
    return extractAskNowInfo(
      askNowContent,
      fields,
      document,
      askNowPageIndex,
      conditionLogicWithScript
    );
  }

  return defaultAskNowInfo;
}

export function extractAskNowInfo(
  askNowContent: Document,
  fields: Element[],
  document: Document,
  askNowPageIndex: number,
  conditionLogicWithScript: ValidationScriptBlock[]
) {
  const askNowNode = askNowContent.getElementsByTagName('innovation-testing-spec')[0];

  if (!askNowNode) {
    return defaultAskNowInfo;
  }

  const fieldNodes = Array.from(fields);
  const children = Array.from(askNowNode.children);
  const unitNameNode = fieldNodes.find((n) => n.getAttribute('field') === 'unit_name');
  const localeNode = fieldNodes.find((n) => n.getAttribute('field') === 'm__language');
  const pageNodesForAskNow = children.filter(
    (node) => node.nodeName === 'page' && node.nodeType === 1
  ) as Element[];

  return {
    askNow: {
      altSetsCount: children.filter((n) => n.nodeName === 'altset').length,
      version: askNowNode.getAttribute('version') ?? '',
      stats: parseStats(pageNodesForAskNow).allStats,
      condition: (() => {
        const condition = askNowNode.getAttribute('condition') as string;
        return condition === 'true' ? '' : prettyfyCondition(document, condition) ?? '';
      })(),
      isAskNowValueNodeAvailable: true,
      askNowPageIndex,
      unitName: extractByLanguage(unitNameNode?.getAttribute('value') as string) ?? '',
      language: localeNode?.getAttribute('value') ?? '',
      availableLanguages: askNowNode.getAttribute('locale') ?? '',
      id: askNowNode.getAttribute('innovation-test-id') ?? '',
      conditionLogicWithScript
    }
  };
}

function extractByLanguage(text: string, lang = 'en') {
  if (typeof text !== 'string') return null;

  const regex = new RegExp(`\\[=:${lang}:([\\s\\S]*?)(?=\\]\\[|\\]$)\\]`, 'i');

  const match = text.match(regex);
  if (match) return match[1].trim();

  // Fallback: if there are no tags at all, return the whole string trimmed
  if (!text.includes('[=:')) return text.trim();

  // Tags exist but the requested language isn't present
  return null;
}

export function prettyfyCondition(xmlDoc: Document, expr: string) {
  // Case 1: quantified (at most/at least/exactly N of {...})
  const quantMatch = expr.match(
    /(at most|at least|exactly|less than|more than)\s+(\d+)\s+of\s*{([^}]*)}/i
  );

  if (quantMatch) {
    const [_, quant, num, inner] = quantMatch;
    const parts = inner.split(',').map((c) => resolveAtomic(xmlDoc, c.trim()));
    return `${quant} ${num} of { ${parts.join(', ')} }`;
  }

  // Case 2: normal logical expression (AND/OR/parentheses)
  return expr
    .split(/(\s+AND\s+|\s+OR\s+|\(|\))/i)
    .map((token) => {
      token = token.trim();
      if (!token || ['AND', 'OR', '(', ')'].includes(token.toUpperCase())) return token;
      return resolveAtomic(xmlDoc, token);
    })
    .join(' ');
}

function resolveAtomic(xmlDoc: Document, token: string) {
  const match = token.match(/^([a-zA-Z0-9_]+)\s*=\s*"?([^"]+)"?$/);

  if (!match) {
    return token;
  }

  const [_, fieldId, value] = match;
  // Find altfield
  const altfield = xmlDoc.querySelector(`altfield[id="${fieldId}"]`);

  if (!altfield) {
    return `${fieldId} = ${value}`;
  }

  // Find altset
  const altsetId = altfield.getAttribute('altset');
  const altset = xmlDoc.querySelector(`altset[id="${altsetId}"]`);

  if (!altset) {
    return `${fieldId} = ${value}`;
  }

  // Match value in altset
  const alt = altset.querySelector(`alt[key="${value}"]`) as Element;

  if (!alt) {
    return `${fieldId}`;
  }

  let resolvedName = extractEnglishLabel(alt.getAttribute('name')!);
  return `${fieldId} = ${resolvedName}`;
}

function extractEnglishLabel(nameAttr: string) {
  const match = nameAttr.match(/\[=:en:([^\]]+)\]/);
  return match ? match[1] : nameAttr; // fallback to raw if no English found
}

//----------------------------------------------------------------------
// SELECTORS
//----------------------------------------------------------------------

const hasOverridesSelector = (state: RootState) =>
  !isEmpty(state.surveyDebugger.clientFeatureFlags.draft);

const clientFeatureFlagsSelector = createSelector(
  [(state: RootState) => state.surveyDebugger.clientFeatureFlags.final],
  (clientFeatureFlags) => {
    return Object.entries(clientFeatureFlags).filter(
      (entry) => entry[0] !== '24cr4_MPC8530_surveys_runtime_debugger'
    ) as Array<[ClientSideRenderingFeatureFlag, boolean]>;
  }
);

//----------------------------------------------------------------------
// HOOKS
//----------------------------------------------------------------------

export function useDebugger() {
  const {
    isRuntimeDebuggerEnabled,
    brandingStyleSheets,
    rawSurveySpec,
    surveySpec,
    loading,
    error
  } = useSelector(surveyDebuggerSelector);

  return {
    isRuntimeDebuggerEnabled,
    brandingStyleSheets,
    surveySpec,
    rawSurveySpec,
    loading,
    error
  };
}

export function useFeatureFlagsDebugger() {
  const dispatch = useAppDispatch();
  const clientFeatureFlags = useAppSelector(clientFeatureFlagsSelector);
  const hasOverrides = useAppSelector(hasOverridesSelector);
  const state = useAppSelector(surveyDebuggerSelector);

  // State

  const hasOverridesForAnySurvey = DebuggerService.hasOverridesForAnySurvey();

  const hasOverrideFor = (id: ClientSideRenderingFeatureFlag) => {
    return !isNil(state.clientFeatureFlags.draft[id]);
  };

  const hasScheduledOverrideFor = (id: ClientSideRenderingFeatureFlag) => {
    if (hasOverrideFor(id)) {
      return state.clientFeatureFlags.draft[id] !== state.clientFeatureFlags.prev[id];
    }
  };

  // Events

  const onChange = (id: ClientSideRenderingFeatureFlag, newValue: boolean) => {
    dispatch(updateClientFeatureFlag({ id, value: newValue }));
  };

  const onResetSurveyOverrides = () => {
    dispatch(resetClientFeatureFlags());
  };

  const onResetAllOverrides = () => {
    dispatch(clearAllOverrides());
  };

  const onSave = () => {
    const newEntry = { featureFlags: state.clientFeatureFlags.draft };
    dispatch(setOverride(newEntry));
  };

  // API

  return {
    // Events
    onChange,
    onSave,
    onResetSurveyOverrides,
    onResetAllOverrides,
    // Predicates
    hasOverrides,
    hasOverrideFor,
    hasScheduledOverrideFor,
    hasOverridesForAnySurvey,
    // Data
    serverFeatureFlags: normalizeSurveyEngineFeatureFlagsList(),
    clientFeatureFlags
  };
}
